<template>
  <div>
    <b-modal
      id="modal-add-voice"
      :title="isUploading ? 'Upload file Audio' : 'Upload Audio'"
      centered
      hide-footer
      @hidden="resetState"
      data-me="rjm-component-modal-add-voice"
    >
      <div v-if="!isUploading" class="upload-container">
        <div class="upload-drop-zone" @dragover.prevent @drop.prevent="handleDrop">
          <div class="upload-icon mb-3">
            <i class="icon-outline_upload"></i>
          </div>
          <div class="text-muted mb-2 font-size-14">{{ $t('rjm.chooseOrDropFile') }}</div>
          <div class="text-muted mb-4">({{ $t('rjm.maxSize') }}, {{ $t('rjm.fileType') }})</div>

          <input
            ref="fileInput"
            type="file"
            class="d-none"
            accept=".mp3,.wav,.aac"
            @change="handleFileSelect"
            data-me="rjm-component-modal-add-voice-input-file"
          />
          <McButton
            variant="outline-primary"
            text="Pilih File"
            @click="$refs.fileInput.click()"
            data-me="rjm-component-modal-add-voice-button-choose-file"
          />
        </div>
      </div>

      <!-- State 2: Progress View -->
      <div v-else class="upload-progress-view">
        <p class="file-name text-center">{{ file ? file.name : '-' }}</p>
        <b-row>
          <b-col sm="12">
            <b-progress
              :value="uploadProgress"
              variant="primary"
              class="mt-2 custom-progress"
            ></b-progress>
          </b-col>
        </b-row>

        <div class="d-flex justify-content-between align-items-center">
          <span class="file-size text-muted">
            {{ formatSize(uploadedSize) }} / {{ formatSize(totalSize) }}
          </span>
          <div class="d-flex align-items-center">
            <span class="progress-percentage mr-3">{{ uploadProgress }} %</span>
          </div>
        </div>
      </div>
    </b-modal>
    <b-modal
      id="modal-audio-full"
      title="Peringatan"
      size="md"
      centered
      data-me="rjm-component-modal-audio-full"
    >
      <div class="mc-modal__body">
        {{ $t('rjm.beLimitMessage') }}
      </div>
      <template #modal-footer="{ cancel }">
        <b-row class="mc-modal__filter-footer mx-0">
          <b-col sm="12" md="12" class="mc-modal__filter-footer-btn mt-0">
            <McButton
              variant="outline-primary"
              @click="cancel()"
              class="me-2"
              :text="$t('global.alert.text.understand')"
              data-me="rjm-component-modal-audio-full-button-understand"
            />
          </b-col>
        </b-row>
      </template>
    </b-modal>
  </div>
</template>

<script>
import McButton from '@/components/McButton'
import { createAudioFile } from '@/services/api/master/rjm'

export default {
  name: 'ModalAddVoice',
  components: {
    McButton,
  },
  data() {
    return {
      isUploading: false,
      file: null,
      uploadProgress: 0,
      uploadedSize: 0,
      totalSize: 0,
      uploadInterval: null,
    }
  },
  methods: {
    handleDrop(e) {
      const files = e.dataTransfer.files
      if (files.length) this.processFile(files[0])
    },
    handleFileSelect(e) {
      const files = e.target.files
      if (files.length) this.processFile(files[0])
    },
    processFile(file) {
      if (
        !file.name.toLowerCase().endsWith('.mp3') &&
        !file.name.toLowerCase().endsWith('.wav') &&
        !file.name.toLowerCase().endsWith('.aac')
      ) {
        this.$mcToast.error('Format file harus .mp3 .wav .aac')
        return
      }
      if (file.size > 1 * 1024 * 1024) {
        this.$mcToast.error(this.$t('rjm.maxSize'))
        return
      }

      this.file = file
      this.totalSize = file.size
      this.uploadedSize = 0
      this.uploadProgress = 0
      this.isUploading = true
      this.performUpload()
    },
    async performUpload() {
      this.simulateProgress()

      try {
        const base64Data = await this.fileToBase64(this.file)
        const base64String = base64Data.split(',')[1]

        const res = await this.$apollo.mutate({
          mutation: createAudioFile,
          variables: {
            input: {
              filename: this.file.name,
              base64: base64String,
            },
          },
        })

        if (res.data.uploadAudioFile.isSuccessful) {
          this.uploadProgress = 100
          this.uploadedSize = this.totalSize
          clearInterval(this.uploadInterval)

          this.$emit('success')

          setTimeout(() => {
            this.$bvModal.hide('modal-add-voice')
            this.$mcToast.success(this.$t('rjm.successUpload'))
          }, 1000)
        } else {
          if (res.data.uploadAudioFile.message === 'Company already have 10 audio files') {
            this.$bvModal.hide('modal-add-voice')
            this.$bvModal.show('modal-audio-full')
            return
          }
          throw new Error(res.data.uploadAudioFile.message || this.$t('rjm.errorUpload'))
        }
      } catch (error) {
        clearInterval(this.uploadInterval)
        this.isUploading = false
        if (error.message === 'Company already have 10 audio files') {
          this.$bvModal.hide('modal-add-voice')
          this.$bvModal.show('modal-audio-full')
          return
        }
        this.$mcToast.error(error.message || this.$t('rjm.errorUpload'))
      }
    },
    simulateProgress() {
      this.uploadInterval = setInterval(() => {
        if (this.uploadProgress < 90) {
          this.uploadProgress += 5
          this.uploadedSize = (this.uploadProgress / 100) * this.totalSize
        }
      }, 200)
    },
    resetState() {
      this.isUploading = false
      this.file = null
      this.uploadProgress = 0
      this.uploadedSize = 0
      this.totalSize = 0
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    },
    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => resolve(reader.result)
        reader.onerror = (error) => reject(error)
      })
    },
    formatSize(bytes) {
      if (bytes === 0) return '0 MB'
      const mb = bytes / (1024 * 1024)
      return mb.toFixed(1) + ' MB'
    },
  },
}
</script>

<style lang="scss" scoped>
.upload-drop-zone {
  border: 2px dashed $teal-100;
  border-radius: 8px;
  padding: 40px 20px;
  text-align: center;
  background-color: $white;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  &:hover {
    border-color: $primary;
  }

  .upload-icon {
    font-size: 24px;
    color: $primary;
  }
}

.upload-progress-view {
  padding: 10px;

  .file-name {
    font-weight: 600;
    font-size: 14px;
  }

  .file-size {
    font-size: 12px;
  }

  .progress-percentage {
    font-size: 16px;
    color: $primary;
  }

  .cursor-pointer {
    cursor: pointer;
  }
}

.custom-progress {
  height: 8px;
  background-color: $teal-100;
}
</style>
